import type { FulfillmentEvent } from '@/infrastructure/FulfillmentAdminClient';
import { kstClock, kstLogStamp } from '@/utils/fulfillmentTime';

/** Whether the row moved goods in, out, or took something back — the wireframe colours by that. */
type ActivityTone = 'in' | 'out' | 'cancel' | 'plain';

export interface ActivityRow {
  id: string;
  at: string;
  action: string;
  tone: ActivityTone;
  sku: string;
  qty: string;
  worker: string;
  memo: string;
}

interface ActivityLabel {
  action: string;
  tone: ActivityTone;
}

/** The operations the desk can cause, named the way the wireframe's log names them. */
const LABELS: Record<string, ActivityLabel> = {
  ADMIN_INBOUND: { action: 'INBOUND', tone: 'in' },
  ADMIN_INBOUND_CANCEL: { action: 'INBOUND Cancelled', tone: 'cancel' },
  ADMIN_OUTBOUND: { action: 'OUTBOUND', tone: 'out' },
  ADMIN_OUTBOUND_CANCEL: { action: 'OUTBOUND Cancelled', tone: 'cancel' },
  ADMIN_LINE_CANCEL: { action: 'LINE Cancelled', tone: 'cancel' },
  RETURN_PUTAWAY: { action: 'Return Restock (Stock added)', tone: 'in' },
  ADMIN_HOLD: { action: 'HOLD Applied', tone: 'cancel' },
  ADMIN_UNHOLD: { action: 'HOLD Released', tone: 'in' },
  ADMIN_ADDRESS_EDIT: { action: 'Address edited', tone: 'plain' },
  TRACKING_CORRECTED: { action: 'Tracking corrected', tone: 'plain' },
};

/** One marker for an absent value across this screen — two glyphs read as two different states. */
export const MISSING = '–';

export const payloadOf = (event: FulfillmentEvent): Record<string, unknown> =>
  event.payload && typeof event.payload === 'object' ? (event.payload as Record<string, unknown>) : {};

const text = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined);

/** An outbound moves the whole order, so it names the count rather than one sku. */
const skuOf = (payload: Record<string, unknown>): string => {
  const sku = text(payload['sku']);
  if (sku) {
    return sku;
  }
  const lines = payload['lines'];
  return Array.isArray(lines) ? `All (${lines.length} SKU)` : MISSING;
};

const count = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined);

/** What one inbound cancel did with the units that had arrived, as both actor logs report it. */
export interface InboundCancelSummary {
  /** The outcome the action names in brackets; absent on events written before the restock was recorded. */
  outcome?: string;
  /** Restocked units when anything went back on the shelf, otherwise the units the cancel took away. */
  qty: number | undefined;
}

/**
 * `qty` on an inbound-cancel event is what had arrived, not what went back on the shelf — reporting it
 * alone made a one-of-three restock read as three restocked, and a write-off read as a restock.
 */
export const summarizeInboundCancel = (payload: Record<string, unknown>): InboundCancelSummary => {
  const arrived = count(payload['qty']);
  if (payload['restock'] === false) {
    return { outcome: 'Written off', qty: arrived };
  }
  if (payload['restock'] !== true) {
    return { qty: arrived };
  }
  const restocked = count(payload['restockQty']) ?? arrived;
  if (arrived !== undefined && restocked !== undefined && restocked < arrived) {
    return { outcome: `Restocked · ${arrived - restocked} written off`, qty: restocked };
  }
  return { outcome: 'Restocked', qty: restocked };
};

/**
 * Whatever the operator typed at the time — a cancel reason, a hold reason, a restock note. The shelf is
 * never a stand-in: it is not something anyone wrote, and it read as one in the memo column.
 */
export const operatorNoteOf = (payload: Record<string, unknown>): string | undefined =>
  text(payload['memo']) ?? text(payload['reason']);

/** `11:32:04` — the barcode feed answers "did that scan land", so it needs seconds and no date. */
export const clockTime = (at: Date): string => kstClock(at.toISOString());

/** `07-09 11:32`, the way the warehouse log reads it — the year is never the question at the desk. */
export const logTimestamp = (iso: string): string => kstLogStamp(iso) ?? iso;

/** Newest first: the desk reads the log to confirm what it just did, not to read a history. */
export const activityRows = (events: FulfillmentEvent[]): ActivityRow[] =>
  [...events]
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
    .map((event) => {
      const payload = payloadOf(event);
      const label = LABELS[event.operation] ?? { action: event.operation, tone: 'plain' as ActivityTone };
      const cancel = event.operation === 'ADMIN_INBOUND_CANCEL' ? summarizeInboundCancel(payload) : undefined;
      const qty = cancel ? cancel.qty : count(payload['qty']);
      return {
        id: event.id,
        at: logTimestamp(event.occurredAt),
        action: cancel?.outcome ? `${label.action} (${cancel.outcome})` : label.action,
        tone: label.tone,
        sku: skuOf(payload),
        qty: qty === undefined ? MISSING : String(qty),
        worker: event.actor || MISSING,
        memo: operatorNoteOf(payload) ?? MISSING,
      };
    });
